import config from '../config';

const SHEETS_API_KEY = config.GOOGLE_API_KEY;
const PUBLICATIONS_SHEET_ID = '1WDZnmKnqSu_Yam6r0VL0nIan8n759ZFbL-ukxdn0EoU';
const RANGE = 'Sheet1!A1:Z'; // Use a wide range to capture all publication data

// Helper to extract the raw Google Sheet ID from a variety of inputs
const extractSheetId = (input) => {
  if (!input) return '';
  console.log('[extractSheetId] Input:', input);
  
  // If it's already just an ID (no http present), return as-is
  if (!input.includes('http')) {
    console.log('[extractSheetId] Input appears to be a raw ID.');
    return input;
  }

  // Try standard /d/<ID>/ pattern first
  const match = input.match(/\/d\/([a-zA-Z0-9-_]+)/);
  if (match && match[1]) {
    console.log('[extractSheetId] Extracted ID via regex:', match[1]);
    return match[1];
  }

  console.log('[extractSheetId] Regex match failed. Trying URL parser fallback...');

  // Fallback: use URL parser and take segment after "d"
  try {
    const urlObj = new URL(input);
    const parts = urlObj.pathname.split('/');
    const dIndex = parts.indexOf('d');
    if (dIndex !== -1 && parts.length > dIndex + 1) {
      return parts[dIndex + 1];
    }
  } catch (e) {
    console.error('[extractSheetId] URL parsing failed:', e);
  }

  console.warn(`[extractSheetId] Could not extract ID from: ${input}. Returning original input.`);
  return input;
};

/**
 * Fetch and parse publications data from the Google Sheet
 * @param {string} sheetId - Optional override sheet ID. If not provided, uses default publications sheet.
 * @returns {Promise<Array>} Array of publication objects
 */
export const fetchPublicationsData = async (sheetId = PUBLICATIONS_SHEET_ID) => {
  try {
    const cleanId = extractSheetId(sheetId);
    console.log('Fetching publications data from spreadsheet ID:', cleanId);
    
    // Fetch data from Google Sheets API
    const response = await fetch(
      `${config.GOOGLE_SHEETS_API}/${cleanId}/values/${RANGE}?key=${SHEETS_API_KEY}`
    );
    
    if (!response.ok) {
      throw new Error(`Google Sheets API request failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.values || data.values.length <= 1) {
      console.warn('No publications data found in spreadsheet or only header row exists');
      return [];
    }
    
    console.log('Raw publications spreadsheet data:', data.values);
    console.log(`Found ${data.values.length - 1} rows of publication data`);
    
    // Get column headers for reference
    const headers = data.values[0];
    console.log('Publications column headers:', headers);
    
    // Find indexes of important columns with flexible name matching
    const findColumnIndex = (possibleNames) => {
      for (const name of possibleNames) {
        const index = headers.findIndex(header => 
          header && typeof header === 'string' && header.toLowerCase().includes(name.toLowerCase())
        );
        if (index >= 0) {
          console.log(`Found column '${name}' at index ${index} with header: '${headers[index]}'`);
          return index;
        }
      }
      console.log(`Column not found for names: ${possibleNames.join(', ')}`);
      return -1;
    };
    
    // Find columns by trying the exact column names from the actual sheet
    const publicationIdIndex = findColumnIndex(['publication id']);
    const eventIndex = findColumnIndex(['event']);
    const publicationSeriesIdIndex = findColumnIndex(['publication series id']);
    const pIdInSeriesIndex = findColumnIndex(['p-id in series']);
    const titleIndex = findColumnIndex(['title']);
    const speakerIndex = findColumnIndex(['speaker']); // This is the author
    const dateIndex = findColumnIndex(['date']);
    const publicationTypeIndex = findColumnIndex(['publication type']);
    const subTypeIndex = findColumnIndex(['sub-type']); // This is the description
    const publicationUrlIndex = findColumnIndex(['publication_url', 'publication url']);
    
    console.log('Publications column indexes found:', {
      publicationId: publicationIdIndex,
      event: eventIndex,
      publicationSeriesId: publicationSeriesIdIndex,
      pIdInSeries: pIdInSeriesIndex,
      title: titleIndex,
      speaker: speakerIndex,
      date: dateIndex,
      publicationType: publicationTypeIndex,
      subType: subTypeIndex,
      publicationUrl: publicationUrlIndex
    });
    
    // Clean and validate URLs - trim whitespace and ensure they have a protocol
    const cleanUrl = (url) => {
      if (!url) return '';
      let cleaned = url.trim();
      // If it looks like a URL but doesn't have a protocol, add https://
      if (cleaned && !cleaned.startsWith('http://') && !cleaned.startsWith('https://')) {
        if (cleaned.includes('drive.google.com') || cleaned.includes('docs.google.com')) {
          cleaned = 'https://' + cleaned;
        }
      }
      return cleaned;
    };
    
    // Helper function to extract year from date string like "June 2024"
    const extractYear = (dateString) => {
      if (!dateString) return 'Unknown Year';
      const yearMatch = dateString.match(/\d{4}/);
      return yearMatch ? yearMatch[0] : 'Unknown Year';
    };

    // Map data rows to publication objects
    const publications = data.values.slice(1).map((row, index) => {
      // Skip empty rows
      if (!row.length || (!row[titleIndex] && !row[0])) {
        return null;
      }

      // Extract data using detected column indexes
      const publicationId = row[publicationIdIndex] || `pub_${index + 1}`;
      const event = row[eventIndex] || 'Uncategorized';
      const publicationSeriesId = row[publicationSeriesIdIndex] || '';
      const pIdInSeries = row[pIdInSeriesIndex] || '';
      const title = row[titleIndex] || `Publication ${index + 1}`;
      const speaker = row[speakerIndex] || ''; // This becomes the author
      const dateString = row[dateIndex] || '';
      const year = extractYear(dateString);
      const publicationType = row[publicationTypeIndex] || 'Book';
      const subType = row[subTypeIndex] || ''; // This becomes the description
      const pdfUrl = row[publicationUrlIndex] ? cleanUrl(row[publicationUrlIndex]) : '';
      
      // Create publication object
      const publication = {
        id: publicationId.toString().trim(),
        year: year.toString().trim(),
        event: event.trim(),
        title: title.trim(),
        type: publicationType.trim(),
        author: speaker.trim(),
        pdfUrl: pdfUrl,
        description: subType.trim(),
        language: 'English', // Default since not specified in sheet
        pages: '', // Not available in current sheet structure
        // Add computed fields
        displayYear: year.toString().trim(),
        typeTag: publicationType.trim().toLowerCase().replace(/\s+/g, '-') || 'book',
        // Additional fields from the sheet
        publicationSeriesId: publicationSeriesId.trim(),
        pIdInSeries: pIdInSeries.toString().trim(),
        originalDate: dateString.trim()
      };
      
      console.log(`Publication ${index + 1}:`, {
        title: publication.title,
        year: publication.year,
        type: publication.type,
        pdfUrl: publication.pdfUrl
      });
      
      return publication;
    });
    
    // Filter out null entries (from empty rows)
    const filteredPublications = publications.filter(publication => publication !== null);
    
    console.log(`Processed ${filteredPublications.length} publications after filtering empty rows`);
    
    // Sort publications by year (newest first) and then by title
    filteredPublications.sort((a, b) => {
      const yearA = parseInt(a.year) || 0;
      const yearB = parseInt(b.year) || 0;
      
      if (yearA !== yearB) {
        return yearB - yearA; // Newest first
      }
      
      // If years are the same, sort by title
      return a.title.localeCompare(b.title);
    });
    
    return filteredPublications;
  } catch (error) {
    console.error('Error fetching publications data:', error);
    throw error;
  }
};


/**
 * Group publications by year
 * @param {Array} publications - Array of publication objects
 * @returns {Object} Publications grouped by year
 */
export const groupPublicationsByYear = (publications) => {
  const grouped = {};
  
  publications.forEach(publication => {
    const year = publication.displayYear;
    if (!grouped[year]) {
      grouped[year] = [];
    }
    grouped[year].push(publication);
  });
  
  // Sort years in descending order (newest first)
  const sortedYears = Object.keys(grouped).sort((a, b) => {
    const yearA = parseInt(a) || 0;
    const yearB = parseInt(b) || 0;
    return yearB - yearA;
  });
  
  const sortedGrouped = {};
  sortedYears.forEach(year => {
    sortedGrouped[year] = grouped[year];
  });
  
  return sortedGrouped;
};

/**
 * Filter publications by type
 * @param {Array} publications - Array of publication objects
 * @param {string} type - Type to filter by
 * @returns {Array} Filtered publications
 */
export const filterPublicationsByType = (publications, type) => {
  if (!type || type === 'all') {
    return publications;
  }
  
  return publications.filter(publication => 
    publication.type.toLowerCase().includes(type.toLowerCase())
  );
};

/**
 * Get all unique publication types
 * @param {Array} publications - Array of publication objects
 * @returns {Array} Array of unique types
 */
export const getPublicationTypes = (publications) => {
  const types = publications.map(pub => pub.type).filter(type => type);
  return [...new Set(types)].sort();
};

/**
 * Search publications by query
 * @param {Array} publications - Array of publication objects
 * @param {string} query - Search query
 * @returns {Array} Array of matching publications
 */
export const searchPublications = (publications, query) => {
  if (!query) {
    return publications;
  }
  
  const lowerCaseQuery = query.toLowerCase();
  
  return publications.filter(publication => {
    return (
      publication.title.toLowerCase().includes(lowerCaseQuery) ||
      publication.author.toLowerCase().includes(lowerCaseQuery) ||
      publication.event.toLowerCase().includes(lowerCaseQuery) ||
      publication.description.toLowerCase().includes(lowerCaseQuery) ||
      publication.type.toLowerCase().includes(lowerCaseQuery)
    );
  });
};

/**
 * Get publication by ID
 * @param {Array} publications - Array of publication objects
 * @param {string} id - Publication ID
 * @returns {Object|null} Publication object or null if not found
 */
export const getPublicationById = (publications, id) => {
  return publications.find(pub => pub.id === id) || null;
};